import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "./api";

const CurrencyContext = createContext(null);

const LS_KEY = "dompryce_currency";

const NICE_DECIMALS = {
  // Currencies that don't use minor units / look weird with decimals
  JPY: 0, KRW: 0, VND: 0, IDR: 0, CLP: 0, HUF: 0, ISK: 0,
};

export function CurrencyProvider({ children }) {
  const [currency, setCurrency] = useState("USD");
  const [symbol, setSymbol] = useState("$");
  const [rates, setRates] = useState({ USD: 1 });
  const [country, setCountry] = useState("US");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Honor user override from prior session
        const saved = typeof window !== "undefined" ? localStorage.getItem(LS_KEY) : null;
        const [geoRes, fxRes] = await Promise.all([
          api.get("/geo"),
          api.get("/fx/rates", { params: { base: "USD" } }),
        ]);
        if (cancelled) return;
        const detected = geoRes.data.currency || "USD";
        const sym = geoRes.data.currency_symbol || "$";
        const cc = geoRes.data.country_code || "US";
        const fxRates = fxRes.data.rates || { USD: 1 };
        setCountry(cc);
        setRates(fxRates);
        const chosen = saved && fxRates[saved] ? saved : (fxRates[detected] ? detected : "USD");
        setCurrency(chosen);
        setSymbol(saved && fxRates[saved] ? symbolFor(saved, sym, detected) : sym);
      } catch (e) {
        // graceful: stay USD
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const formatPrice = useMemo(() => (usd) => {
    if (usd == null || isNaN(usd)) return "—";
    const rate = rates[currency] ?? 1;
    const value = usd * rate;
    const decimals = NICE_DECIMALS[currency] ?? 2;
    const formatted = value.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    return `${symbol}${formatted}`;
  }, [currency, rates, symbol]);

  const setUserCurrency = (code) => {
    if (!rates[code]) return;
    setCurrency(code);
    setSymbol(symbolFor(code, "", code));
    localStorage.setItem(LS_KEY, code);
  };

  return (
    <CurrencyContext.Provider value={{ currency, symbol, rates, country, ready, formatPrice, setUserCurrency }}>
      {children}
    </CurrencyContext.Provider>
  );
}

function symbolFor(code, fallback, detected) {
  // Lightweight symbol map mirrored from backend
  const map = {
    USD: "$", EUR: "€", GBP: "£", INR: "₹", JPY: "¥", CNY: "¥", AUD: "A$",
    CAD: "C$", NZD: "NZ$", SGD: "S$", HKD: "HK$", CHF: "CHF", SEK: "kr",
    NOK: "kr", DKK: "kr", PLN: "zł", BRL: "R$", MXN: "MX$", ZAR: "R",
    AED: "AED", SAR: "SAR", TRY: "₺", RUB: "₽", KRW: "₩", THB: "฿",
    IDR: "Rp", MYR: "RM", PHP: "₱", VND: "₫", ILS: "₪",
  };
  return map[code] || (code === detected ? fallback : code + " ");
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be used within CurrencyProvider");
  return ctx;
}
